import styled from "styled-components";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { LoaderWithOverlay, SummaryItem } from "components/atoms";
import { IconName } from "interfaces/icons";

import {
	getDashboardReportData,
	getDashboardReportLoading,
} from "state/modules/dashboard";
import { MAIN_THEME } from "styles/colors";
import SummaryItemThreeValues from "../atoms/SummaryItemThreeValues";

const Container = styled.div`
	display: grid;
	grid-template-columns: repeat(5, 1fr);
	gap: 15px;
	width: 100%;
	position: relative;

	@media (max-width: 1200px) {
		grid-template-columns: repeat(3, 1fr);
	}

	@media (max-width: 768px) {
		grid-template-columns: repeat(2, 1fr);
		gap: 10px;
	}

	@media (max-width: 480px) {
		grid-template-columns: 1fr;
		gap: 10px;
	}
`;

const DashboardSummary = () => {
	const report = useSelector(getDashboardReportData);
	const isLoading = useSelector(getDashboardReportLoading);

	const summaryItems = useMemo(() => {
		if (report) {
			const { calculations } = report;
			return [
				{
					title: "Owner Payouts",
					value: `${calculations?.totalInCurrency?.totalIncomeInCurrency || 0}$`,
					value1: `${calculations?.totalIncomePerCurrency?.AMD || 0}֏`,
					value2: `${calculations?.totalIncomePerCurrency?.USD || 0}$`,
					icon: IconName.DOLLAR,
				},
				{
					title: "Owner Net Income",
					value: `${calculations?.totalInCurrency?.netOwnerIncomeInCurrency || 0}$`,
					icon: IconName.WALLET,
				},
				{
					title: "Expenses",
					value: `${calculations?.totalInCurrency?.totalExpensesInCurrency || 0}$`,
					value1: `${calculations?.totalExpensesPerCurrency?.AMD || 0}֏`,
					value2: `${calculations?.totalExpensesPerCurrency?.USD || 0}$`,
					icon: IconName.TRENDING_UP,
				},
				{
					title: "Reservations",
					value: `${calculations?.reservationPeriod?.numberOfReservations || 0}`,
					icon: IconName.CALENDAR,
				},
				{
					title: "Cleaning",
					value: `${calculations?.totalInCurrency?.totalCleaningInCurrency || 0}$`,
					value1: `${calculations?.totalCleaningPerCurrency?.AMD || 0}֏`,
					value2: `${calculations?.totalCleaningPerCurrency?.USD || 0}$`,
					icon: IconName.CLEANING,
				},
				{
					title: "Occupancy Rate",
					value: `${Math.round(calculations?.occupancyRate || 0)}%`,
					icon: IconName.CHART,
				},
				{
					title: "ADR",
					value: `${calculations?.totalInCurrency?.adr || 0}$`,
					icon: IconName.DOLLAR,
				},
				{
					title: "RevPAR",
					value: `${calculations?.totalInCurrency?.revPar || 0}$`,
					icon: IconName.TRENDING_UP,
				},
				{
					title: "ALOS",
					value: `${calculations?.alos || 0}days`,
					icon: IconName.CALENDAR,
				},
			];
		}

		return [];
	}, [report]);

	return (
		<Container>
			{summaryItems.map((item) =>
				item.value1 && item.value2 ? (
					<SummaryItemThreeValues
						key={item.title}
						value={item.value}
						title={item.title}
						value1={item.value1}
						value2={item.value2}
						icon={item.icon}
					/>
				) : (
					<SummaryItem
						key={item.title}
						value={item.value}
						title={item.title}
						icon={item.icon}
					/>
				)
			)}
			{isLoading ? <LoaderWithOverlay /> : null}
		</Container>
	);
};

export default DashboardSummary;
